import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import type { DepartmentRecord, RoleTemplateRecord } from '@/types/entities';

type DepartmentItem = DepartmentRecord;
type RoleTemplateItem = RoleTemplateRecord;
import { List, Show, Create, Edit, SimpleForm, TextInput, SelectInput, ReferenceInput, Filter, SearchInput, useGetList, useInput, useRecordContext } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, LinearProgress, Alert, Grid, Divider, Avatar, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton, Tooltip, useMediaQuery, TablePagination, Button, TextField as MuiTextField } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Business, AccountTree, People, Assignment, CheckCircle, Schedule, Cancel, Warning, Build, Category, Person, Add, Delete } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';

const DEPARTMENT_STATUS = ['Active', 'Inactive', 'Archived'];
const ROLE_TEMPLATE_STATUS = ['Active', 'Inactive', 'Archived'];
const SENIORITY_LEVELS = ['Top Management', 'Senior Management', 'Professionally Qualified', 'Skilled Technical', 'Semi-Skilled', 'Unskilled'];
const CONTRACT_TYPES = ['Permanent', 'Fixed-Term', 'Part-Time', 'Seasonal', 'Independent Contractor'];
const STATUTORY_DOCUMENT_TYPES = ['Police Clearance Certificate', 'Credit Check', 'Reference Checks', 'Medical Fitness Certificate', 'Driver\'s License', 'Proof of Qualifications', 'Work Visa/Permit'];

const STATUS_COLORS_DEPT: Record<string, ChipColor> = {
  Active: 'success',
  Inactive: 'default',
  Archived: 'default',
};

const STATUS_COLORS_TEMPLATE: Record<string, ChipColor> = {
  Active: 'success',
  Inactive: 'warning',
  Archived: 'default',
};

export const DepartmentList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: departments, isLoading } = useGetList('departments', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const stats = {
    total: departments?.length ?? 0,
    active: departments?.filter((d: DepartmentItem) => d.status === 'Active').length ?? 0,
    inactive: departments?.filter((d: DepartmentItem) => d.status === 'Inactive').length ?? 0,
    archived: departments?.filter((d: DepartmentItem) => d.status === 'Archived').length ?? 0,
  };

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Departments"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Inactive', value: String(stats.inactive), color: 'default' },
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {departments?.map((dept: DepartmentItem) => (
            <Paper key={dept.id} sx={{ p: 2 }} onClick={() => navigate(`/departments/${dept.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main', fontSize: 14 }}>
                    <Business sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{dept.departmentName}</Typography>
                    <Typography variant="caption" color="text.secondary">{dept.departmentCode} · {dept.costCentre}</Typography>
                  </Box>
                </Box>
                <Chip label={dept.status} size="small" color={STATUS_COLORS_DEPT[dept.status]} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">Head: {dept.headOfDepartmentId || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{dept.departmentName}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Departments"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Inactive', value: String(stats.inactive), color: 'default' },
          { label: 'Archived', value: String(stats.archived), color: 'default' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Head</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employees</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cost Centre</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departments?.map((dept: DepartmentItem) => (
              <TableRow
                key={dept.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/departments/${dept.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 12 }}>
                      <Business sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{dept.departmentName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{dept.departmentCode}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{dept.parentDepartmentId || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{dept.headOfDepartmentId || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{'—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{dept.costCentre || '—'}</Typography>
                </TableCell>
                <TableCell>
                <Chip label={dept.status} size="small" color={STATUS_COLORS_DEPT[dept.status]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={departments?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!departments || departments.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No departments found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const DepartmentFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search department name or code" />
    <SelectInput source="status" choices={DEPARTMENT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

const DepartmentShowContent = () => {
  const record = useRecordContext();
  const { data: roleTemplates, total: templateCount } = useGetList('roletemplates', { filter: { 'departmentId': record?._id || '' }, pagination: { page: 1, perPage: 100 } });
  if (!record) return null;

  return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontSize: 24 }}>
              <Business sx={{ fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>{record.departmentName}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip label={record.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Chip label={record.departmentCode} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Department ID</Typography>
              <Typography variant="h6" fontWeight={700}>{record.departmentCode || 'N/A'}</Typography>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountTree color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Department Details</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Department Code</Typography>
                  <Typography variant="body1" fontWeight={500}>{record.departmentCode || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Cost Centre</Typography>
                  <Typography variant="body1">{record.costCentre || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Parent Department</Typography>
                  <Typography variant="body1">
                    {record.parentDepartmentId ? (
                      <Chip label={record.parentDepartmentId} size="small" component={Link} to={`/departments/${record.parentDepartmentId}/show`} clickable />
                    ) : '— (Root)'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Head of Department</Typography>
                  <Typography variant="body1">{record.headOfDepartmentId || '—'}</Typography>
                </Grid>
              </Grid>
              {record.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{record.description}</Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assignment color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Role Templates ({templateCount ?? 0})</Typography>
              </Box>
              {roleTemplates && roleTemplates.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><Typography variant="caption" fontWeight={700}>Role Title</Typography></TableCell>
                      <TableCell><Typography variant="caption" fontWeight={700}>NQF</Typography></TableCell>
                      <TableCell><Typography variant="caption" fontWeight={700}>Seniority</Typography></TableCell>
                      <TableCell><Typography variant="caption" fontWeight={700}>Contract Types</Typography></TableCell>
                      <TableCell><Typography variant="caption" fontWeight={700}>Employees</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roleTemplates.map((template: RoleTemplateItem) => (
                      <TableRow key={template.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            component={Link}
                            to={`/role-templates/${template.id}/show`}
                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                          >
                            {template.roleTitle}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{template.minimumNqfLevel ? `Level ${template.minimumNqfLevel}` : '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{template.seniorityLevel || '—'}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(template.availableContractTypes || []).map((ct: string) => (
                              <Chip key={ct} label={ct} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2">{0}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No role templates defined for this department</Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Chip
                  component={Link}
                  to={`/role-templates?department=${encodeURIComponent(record.id)}`}
                  clickable
                  label="View All Templates"
                  icon={<Assignment />}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Quick Stats</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">Role Templates</Typography></TableCell>
                    <TableCell><Typography variant="body1" fontWeight={700}>{templateCount ?? 0}</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">Status</Typography></TableCell>
                    <TableCell><Chip label={record.status} size="small" color={STATUS_COLORS_DEPT[record.status]} /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Build color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Quick Actions</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  component={Link}
                  to={`/role-templates/create?departmentId=${encodeURIComponent(record.id)}&departmentName=${encodeURIComponent(record.departmentName)}`}
                  clickable
                  label="Add Role Template"
                  icon={<Assignment />}
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  component={Link}
                  to={`/organization/departments/${record.id}/edit`}
                  clickable
                  label="Edit Department"
                  icon={<Build />}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
};

export const DepartmentShow = () => (
  <Show>
    <DepartmentShowContent />
  </Show>
);

export const DepartmentCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Create a new department within the organisational structure. Departments can be nested to reflect reporting lines.
      </Alert>

      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Department Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="departmentName" label="Department Name *" fullWidth placeholder="e.g., Human Resources" />
        <TextInput source="departmentCode" label="Department Code *" fullWidth placeholder="e.g., HR" helperText="Unique code for this department" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <ReferenceInput source="parentDepartmentId" reference="departments" label="Parent Department" fullWidth>
          <SelectInput optionText="departmentName" />
        </ReferenceInput>
        <TextInput source="headOfDepartmentId" label="Head of Department ID" fullWidth placeholder="Employee URN" helperText="Reference to employee who heads this department" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="costCentre" label="Cost Centre" fullWidth placeholder="e.g., CC-HR-001" />
        <SelectInput source="status" choices={DEPARTMENT_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" label="Status" />
      </Box>
      <TextInput source="description" label="Description" multiline fullWidth rows={2} placeholder="Brief description of department purpose" sx={{ mb: 2 }} />
    </SimpleForm>
  </Create>
);

export const DepartmentEdit = () => (
  <Edit>
    <SimpleForm>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Department Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="departmentName" label="Department Name" fullWidth />
        <TextInput source="departmentCode" label="Department Code" fullWidth />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <ReferenceInput source="parentDepartmentId" reference="departments" label="Parent Department" fullWidth>
          <SelectInput optionText="departmentName" />
        </ReferenceInput>
        <TextInput source="headOfDepartmentId" label="Head of Department ID" fullWidth />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="costCentre" label="Cost Centre" fullWidth />
        <SelectInput source="status" choices={DEPARTMENT_STATUS.map(s => ({ id: s, name: s }))} fullWidth label="Status" />
      </Box>
      <TextInput source="description" label="Description" multiline fullWidth rows={2} sx={{ mb: 2 }} />
    </SimpleForm>
  </Edit>
);

export const RoleTemplateList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: templates, isLoading } = useGetList('roletemplates', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const stats = {
    total: templates?.length ?? 0,
    active: templates?.filter((t: RoleTemplateItem) => t.status === 'Active').length ?? 0,
    inactive: templates?.filter((t: RoleTemplateItem) => t.status === 'Inactive').length ?? 0,
    archived: templates?.filter((t: RoleTemplateItem) => t.status === 'Archived').length ?? 0,
  };

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Role Templates"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {templates?.map((template: RoleTemplateItem) => (
            <Paper key={template.id} sx={{ p: 2 }} onClick={() => navigate(`/organization/role-templates/${template.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    <Assignment sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{template.roleTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">{template.roleCode}</Typography>
                  </Box>
                </Box>
                <Chip label={template.status} size="small" color={STATUS_COLORS_TEMPLATE[template.status]} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">{template.departmentId}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">Level {template.minimumNqfLevel || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{0} employees</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Role Templates"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
          { label: 'Archived', value: String(stats.archived), color: 'default' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Min NQF</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Seniority</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employees</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates?.map((template: RoleTemplateItem) => (
              <TableRow
                key={template.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/organization/role-templates/${template.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      <Assignment sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{template.roleTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">{template.seniorityLevel}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{template.roleCode}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{template.departmentId}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">Level {template.minimumNqfLevel || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{template.seniorityLevel || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{0}</Typography>
                </TableCell>
                <TableCell>
                <Chip label={template.status} size="small" color={STATUS_COLORS_TEMPLATE[template.status]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={templates?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!templates || templates.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No role templates found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const RoleTemplateFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search role title or code" />
    <ReferenceInput source="departmentId" reference="departments" label="Department">
      <SelectInput optionText="departmentName" />
    </ReferenceInput>
    <SelectInput source="seniorityLevel" choices={SENIORITY_LEVELS.map(s => ({ id: s, name: s }))} label="Seniority Level" />
    <SelectInput source="status" choices={ROLE_TEMPLATE_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

export const RoleTemplateShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    const r = record as RoleTemplateRecord;

    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>
              <Assignment sx={{ fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>{r.roleTitle}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip label={r.roleCode} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Chip label={r.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Template ID</Typography>
              <Typography variant="h6" fontWeight={700}>{r.roleCode || 'N/A'}</Typography>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assignment color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Key Parameters</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Minimum NQF Level</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {r.minimumNqfLevel ? `Level ${r.minimumNqfLevel}` : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Seniority Level</Typography>
                  <Chip label={r.seniorityLevel || '—'} size="small" sx={{ mt: 0.5 }} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Available Contract Types</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {(r.availableContractTypes || []).map((ct: string) => (
                      <Chip key={ct} label={ct} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {r.statutoryDocumentRequirements && r.statutoryDocumentRequirements.length > 0 && (
              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircle color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Statutory Document Requirements</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {r.statutoryDocumentRequirements.map((doc: string) => (
                    <Chip key={doc} label={doc} size="small" icon={<CheckCircle fontSize="small" />} />
                  ))}
                </Box>
              </Paper>
            )}

            {r.dutyDefinitions && r.dutyDefinitions.length > 0 && (
              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Assignment color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Duty Definitions</Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 60, fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.dutyDefinitions.map((duty: string, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                          <TableCell>{duty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Department</Typography>
              </Box>
              {r.departmentId && (
                <Chip
                  component={Link}
                  to={`/departments/${r.departmentId}/show`}
                  clickable
                  label={r.departmentId}
                  icon={<Business />}
                  sx={{ justifyContent: 'flex-start' }}
                />
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Utilisation</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">Employees</Typography></TableCell>
                    <TableCell><Typography variant="body1" fontWeight={700}>{0}</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">Status</Typography></TableCell>
                    <TableCell><Chip label={r.status} size="small" color={STATUS_COLORS_TEMPLATE[r.status]} /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Build color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Quick Actions</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  component={Link}
                  to={`/role-templates/${r.id}/edit`}
                  clickable
                  label="Edit Template"
                  icon={<Build />}
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  component={Link}
                  to={`/role-templates/create?cloneFrom=${encodeURIComponent(r.id)}&departmentId=${encodeURIComponent(r.departmentId)}`}
                  clickable
                  label="Clone Template"
                  icon={<Assignment />}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }} />
);

const DutyDefinitionsInput = (props: { source: string; sx?: Record<string, unknown> }) => {
  const { field } = useInput(props);
  const [newDuty, setNewDuty] = useState('');

  const normalize = (value: unknown) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) return value.split('\n').filter(Boolean);
    return [];
  };

  const duties = normalize(field.value);

  const handleAdd = () => {
    if (newDuty.trim()) {
      field.onChange([...duties, newDuty.trim()]);
      setNewDuty('');
    }
  };

  const handleRemove = (index: number) => {
    field.onChange(duties.filter((_: unknown, i: number) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <MuiTextField
          value={newDuty}
          onChange={(e) => setNewDuty(e.target.value)}
          placeholder="Enter a duty description..."
          size="small"
          fullWidth
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
        />
        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAdd} disabled={!newDuty.trim()}>
          Add
        </Button>
      </Box>
      {duties.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60, fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duty</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {duties.map((duty: string, index: number) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                  <TableCell>{duty}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleRemove(index)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="caption" color="text.secondary">No duties defined yet. Add a duty above.</Typography>
      )}
    </Box>
  );
};

export const RoleTemplateCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Create a role template to standardise hiring criteria for positions within a department. Templates define NQF requirements, contract types, and statutory document requirements.
      </Alert>

      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Template Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <ReferenceInput source="departmentId" reference="departments" label="Department *" fullWidth>
          <SelectInput optionText="departmentName" />
        </ReferenceInput>
        <TextInput source="roleTitle" label="Role Title *" fullWidth placeholder="e.g., HR Administrator" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="roleCode" label="Role Code *" fullWidth placeholder="e.g., HR-ADMIN" helperText="Unique code for this role template" />
        <SelectInput source="status" choices={ROLE_TEMPLATE_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" label="Status" />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Qualification Requirements</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <SelectInput
          source="minimumNqfLevel"
          choices={[{ id: '', name: 'No minimum' }, ...Array.from({ length: 10 }, (_, i) => ({ id: String(i + 1), name: `Level ${i + 1}` }))]}
          fullWidth
          label="Minimum NQF Level"
        />
        <SelectInput source="seniorityLevel" choices={SENIORITY_LEVELS.map(s => ({ id: s, name: s }))} fullWidth label="Seniority Level" />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Available Contract Types *</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {CONTRACT_TYPES.map(ct => (
          <Chip key={ct} label={ct} clickable sx={{ cursor: 'pointer' }} />
        ))}
      </Box>
      <TextInput source="availableContractTypes" label="Contract Types (comma-separated)" fullWidth placeholder="Permanent, Fixed-Term" helperText="At least one contract type required" sx={{ mb: 2 }} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Statutory Document Requirements (Optional)</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {STATUTORY_DOCUMENT_TYPES.map(doc => (
          <Chip key={doc} label={doc} clickable sx={{ cursor: 'pointer' }} />
        ))}
      </Box>
      <TextInput source="statutoryDocumentRequirements" label="Required Documents (comma-separated)" fullWidth placeholder="Police Clearance Certificate, Credit Check" sx={{ mb: 2 }} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Duty Definitions</Typography>
      <DutyDefinitionsInput source="dutyDefinitions" sx={{ mb: 2 }} />
    </SimpleForm>
  </Create>
);

export const RoleTemplateEdit = () => (
  <Edit>
    <SimpleForm>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Template Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <ReferenceInput source="departmentId" reference="departments" label="Department" fullWidth>
          <SelectInput optionText="departmentName" />
        </ReferenceInput>
        <TextInput source="roleTitle" label="Role Title" fullWidth />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="roleCode" label="Role Code" fullWidth />
        <SelectInput source="status" choices={ROLE_TEMPLATE_STATUS.map(s => ({ id: s, name: s }))} fullWidth label="Status" />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Qualification Requirements</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <SelectInput
          source="minimumNqfLevel"
          choices={[{ id: '', name: 'No minimum' }, ...Array.from({ length: 10 }, (_, i) => ({ id: String(i + 1), name: `Level ${i + 1}` }))]}
          fullWidth
          label="Minimum NQF Level"
        />
        <SelectInput source="seniorityLevel" choices={SENIORITY_LEVELS.map(s => ({ id: s, name: s }))} fullWidth label="Seniority Level" />
      </Box>

      <Divider sx={{ my: 2 }} />
      <TextInput source="availableContractTypes" label="Contract Types" fullWidth sx={{ mb: 2 }} />

      <Divider sx={{ my: 2 }} />
      <TextInput source="statutoryDocumentRequirements" label="Required Documents" multiline fullWidth rows={2} sx={{ mb: 2 }} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>Duty Definitions</Typography>
      <DutyDefinitionsInput source="dutyDefinitions" sx={{ mb: 2 }} />
    </SimpleForm>
  </Edit>
);